import {
  BodyText,
  MainMenu,
  MainMenuItem,
  useMatchMedia,
} from '@make-software/cspr-ui';
import {
  ACCOUNTS_LIST_PATH,
  ANALYTICS_PATH,
  BLOCK_LIST_PATH,
  CONTRACT_LIST_PATH,
  DELEGATE_STAKE_PATH,
  DEPLOY_LIST_PATH,
  FAUCET_PATH,
  HOME_PATH,
  PEERS_PATH,
  TRANSACTION_LIST_PATH,
  TRANSFER_CSPR_PATH,
  UNDELEGATE_STAKE_PATH,
  VALIDATOR_LIST_PATH,
} from '../../../router/paths';
import { NavAccordion, RouterNavLink } from '../../../components';
import React, { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { NavDropdownV2 } from '../../../components/nav-dropdown/nav-dropdown-v2';
import { NavLink as ReactRouterNavLink } from 'react-router-dom';
import { NavMenuContext } from './nav-menu.context';
import { useGetCasperNetwork } from '../../../services/casper-network-context.tsx';

const MainMenuContainer = styled.div<{ isHome: boolean }>(({ theme, isHome }) =>
  theme.withMedia({
    position: 'relative',
    padding: 0,
    margin: isHome ? ['-85px', '-35px', 0] : 0,
    width: ['100%', '100%', 'auto'],

    '& li': {
      width: ['100%', '100%', 'auto'],

      ':last-child': {
        paddingRight: 0,
      },
    },
  })
);

const backgroundAccordionColor = 'rgba(26, 25, 25, 0.2)';

export const StyledReactRouterNavLink = styled(ReactRouterNavLink)(
  ({ theme }) =>
    theme.withMedia({
      minWidth: '160px',
      color: [
        theme.styleguideColors.contentTertiary,
        theme.styleguideColors.contentTertiary,
        'inherit',
      ],
      '&.active > span': {
        fontWeight: theme.typography.fontWeight.semiBold,
      },
      '& > span:hover': {
        color: [
          theme.styleguideColors.contentOnFill,
          theme.styleguideColors.contentOnFill,
          theme.styleguideColors.contentBlue,
        ],
      },
      '&:hover > span': {
        color: [
          theme.styleguideColors.contentOnFill,
          theme.styleguideColors.contentOnFill,
          theme.styleguideColors.contentBlue,
        ],
      },
    })
);

const useGetMenuItems = () => {
  const { t } = useTranslation();
  const { setMenuVisible } = useContext(NavMenuContext);

  const handleClick = () => {
    setMenuVisible(false);
  };

  const moreItems = [
    {
      component: config.with_faucet ? (
        <StyledReactRouterNavLink to={FAUCET_PATH}>
          <BodyText size={3}>
            <Trans t={t}>Faucet</Trans>
          </BodyText>
        </StyledReactRouterNavLink>
      ) : null,
      handleClick,
    },
    {
      component: (
        <StyledReactRouterNavLink to={ANALYTICS_PATH}>
          <BodyText size={3}>
            <Trans t={t}>Analytics</Trans>
          </BodyText>
        </StyledReactRouterNavLink>
      ),
      handleClick,
    },
    {
      component: (
        <StyledReactRouterNavLink to={PEERS_PATH}>
          <BodyText size={3}>
            <Trans t={t}>Connected Peers</Trans>
          </BodyText>
        </StyledReactRouterNavLink>
      ),
      handleClick,
    },
  ];

  const walletItems = [
    {
      component: (
        <StyledReactRouterNavLink to={TRANSFER_CSPR_PATH}>
          <BodyText size={3}>
            <Trans t={t}>Transfer CSPR</Trans>
          </BodyText>
        </StyledReactRouterNavLink>
      ),
      handleClick,
    },
    {
      component: (
        <StyledReactRouterNavLink to={DELEGATE_STAKE_PATH}>
          <BodyText size={3}>
            <Trans t={t}>Delegate Stake</Trans>
          </BodyText>
        </StyledReactRouterNavLink>
      ),
      handleClick,
    },
    {
      component: (
        <StyledReactRouterNavLink to={UNDELEGATE_STAKE_PATH}>
          <BodyText size={3}>
            <Trans t={t}>Undelegate Stake</Trans>
          </BodyText>
        </StyledReactRouterNavLink>
      ),
      handleClick,
    },
  ];

  return {
    moreItems,
    walletItems,
  };
};

const MoreMenuAccordion = () => {
  const { t } = useTranslation();
  const { moreItems } = useGetMenuItems();
  const moreTitle = ({ isOpen }) => (
    <BodyText size={3} variation={isOpen ? 'white' : 'gray'}>
      <Trans t={t}>More</Trans>
    </BodyText>
  );

  return (
    <li>
      <NavAccordion
        itemList={moreItems}
        backgroundColor={backgroundAccordionColor}
      >
        {moreTitle}
      </NavAccordion>
    </li>
  );
};

const MoreMenuDropdown = () => {
  const { t } = useTranslation();
  const { moreItems } = useGetMenuItems();
  return (
    <MainMenuItem>
      <NavDropdownV2
        stickToRight
        itemList={moreItems}
        title={
          <BodyText size={3}>
            <Trans t={t}>More</Trans>
          </BodyText>
        }
      />
    </MainMenuItem>
  );
};

const WalletMenuAccordion = () => {
  const { t } = useTranslation();
  const { walletItems } = useGetMenuItems();
  const moreTitle = ({ isOpen }) => (
    <BodyText size={3} variation={isOpen ? 'white' : 'gray'}>
      <Trans t={t}>Wallet</Trans>
    </BodyText>
  );

  return (
    <li>
      <NavAccordion
        itemList={walletItems}
        backgroundColor={backgroundAccordionColor}
      >
        {moreTitle}
      </NavAccordion>
    </li>
  );
};

const WalletMenuDropdown = () => {
  const { t } = useTranslation();
  const { walletItems } = useGetMenuItems();
  return (
    <MainMenuItem>
      <NavDropdownV2
        stickToRight
        itemList={walletItems}
        title={
          <BodyText size={3}>
            <Trans t={t}>Wallet</Trans>
          </BodyText>
        }
      />
    </MainMenuItem>
  );
};

export const MainNavigationMenu = ({ isHome }: { isHome: boolean }) => {
  const { t } = useTranslation();
  const { getLocalisationForNetworkVersion, networkMajorVersion } =
    useGetCasperNetwork();
  const { common } = getLocalisationForNetworkVersion;
  const { setMenuVisible } = useContext(NavMenuContext);

  const moreItemDropdown = <MoreMenuDropdown />;
  const walletItemDropdown = <WalletMenuDropdown />;
  const moreItemAccordion = <MoreMenuAccordion />;
  const walletItemAccordion = <WalletMenuAccordion />;

  const handleOnClick = () => {
    setMenuVisible(false);
  };

  const moreItem = useMatchMedia(
    [moreItemAccordion, moreItemAccordion, moreItemDropdown],
    [t]
  );
  const walletItem = useMatchMedia(
    [walletItemAccordion, walletItemAccordion, walletItemDropdown],
    [t]
  );

  return (
    <MainMenuContainer isHome={isHome}>
      <MainMenu>
        <MainMenuItem>
          <RouterNavLink to={HOME_PATH} onClick={handleOnClick}>
            <Trans t={t}>Home</Trans>
          </RouterNavLink>
        </MainMenuItem>
        <MainMenuItem>
          <RouterNavLink to={BLOCK_LIST_PATH} onClick={handleOnClick}>
            <Trans t={t}>Blocks</Trans>
          </RouterNavLink>
        </MainMenuItem>
        <MainMenuItem>
          <RouterNavLink
            to={
              networkMajorVersion === '1'
                ? DEPLOY_LIST_PATH
                : TRANSACTION_LIST_PATH
            }
            onClick={handleOnClick}
          >
            {t(common.deploys)}
          </RouterNavLink>
        </MainMenuItem>
        <MainMenuItem>
          <RouterNavLink to={CONTRACT_LIST_PATH} onClick={handleOnClick}>
            <Trans t={t}>Contracts</Trans>
          </RouterNavLink>
        </MainMenuItem>
        <MainMenuItem>
          <span className="accounts-page-spotlight-target">
            <RouterNavLink to={ACCOUNTS_LIST_PATH} onClick={handleOnClick}>
              <Trans t={t}>Accounts</Trans>
            </RouterNavLink>
          </span>
        </MainMenuItem>
        <MainMenuItem>
          <RouterNavLink to={VALIDATOR_LIST_PATH} onClick={handleOnClick}>
            <Trans t={t}>Validators</Trans>
          </RouterNavLink>
        </MainMenuItem>
        {moreItem}
        {walletItem}
      </MainMenu>
    </MainMenuContainer>
  );
};
